import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from '@ibk/shared-translation';
import { ChannelEnum, TemplateEnum } from '@ibk/shared-types';
import { maskEmail, maskPhone } from '@ibk/shared-utils';
import { useHasValidTokenMutation, useSendEmailTokenMutation, useSendPhoneTokenMutation, useSendWhatsappTokenMutation, } from '../../../../Onboarding/services';
import { useResetPasswordData } from '../../../hooks';
const TOKEN_LENGTH = 6;
const COUNTDOWN_SECONDS = 60;
const INPUT_ID_PREFIX = 'reset-password-token-input';
const createEmptyToken = () => Array(TOKEN_LENGTH).fill('');
export function useTokenValidation({ source, email, phone, onContinue }) {
    const { t } = useTranslation();
    const { cpf, deviceId } = useResetPasswordData();
    const [token, setToken] = useState(createEmptyToken);
    const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);
    const [isCounting, setIsCounting] = useState(true);
    const [tokenError, setTokenError] = useState(undefined);
    const [hasValidToken, { isLoading: isValidating }] = useHasValidTokenMutation();
    const [sendEmailToken, { isLoading: isResendingEmail }] = useSendEmailTokenMutation();
    const [sendPhoneToken, { isLoading: isResendingPhone }] = useSendPhoneTokenMutation();
    const [sendWhatsappToken, { isLoading: isResendingWhatsapp }] = useSendWhatsappTokenMutation();
    const [isWhatsappMode, setIsWhatsappMode] = useState(false);
    const isSmsSource = source === 'sms';
    const maskedEmail = maskEmail(email);
    const maskedPhone = maskPhone(phone);
    const cleanDocumentNumber = cpf.replace(/\D/g, '');
    const getInputId = useCallback((index) => `${INPUT_ID_PREFIX}-${source}-${index}`, [source]);
    const resetCountdown = useCallback(() => {
        setCountdown(COUNTDOWN_SECONDS);
        setIsCounting(true);
    }, []);
    const resetState = useCallback(() => {
        setToken(createEmptyToken());
        setTokenError(undefined);
        resetCountdown();
        setIsWhatsappMode(false);
    }, [resetCountdown]);
    const canSendToken = Boolean(deviceId && cpf);
    useEffect(() => {
        resetState();
    }, [source, resetState]);
    useEffect(() => {
        if (!isCounting)
            return;
        const timer = setInterval(() => {
            setCountdown(prev => {
                if (prev <= 1) {
                    setIsCounting(false);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(timer);
    }, [isCounting]);
    const handleTokenChange = useCallback((index, value) => {
        const upperValue = value.toUpperCase();
        if (!/^[A-Z0-9]*$/.test(upperValue))
            return;
        setTokenError(prev => (prev ? undefined : prev));
        setToken(prev => {
            const newToken = [...prev];
            newToken[index] = upperValue.slice(-1);
            return newToken;
        });
        if (upperValue && index < TOKEN_LENGTH - 1) {
            const nextInput = document.getElementById(getInputId(index + 1));
            nextInput?.focus();
        }
    }, [getInputId]);
    const handleKeyDown = useCallback((index, e) => {
        if (e.key === 'Backspace' && !token[index] && index > 0) {
            const prevInput = document.getElementById(getInputId(index - 1));
            prevInput?.focus();
        }
    }, [token, getInputId]);
    const handlePaste = useCallback((e) => {
        e.preventDefault();
        const pastedData = e.clipboardData
            .getData('text')
            .toUpperCase()
            .replace(/[^A-Z0-9]/g, '')
            .slice(0, TOKEN_LENGTH);
        const newToken = createEmptyToken();
        pastedData.split('').forEach((char, index) => {
            if (index < TOKEN_LENGTH) {
                newToken[index] = char;
            }
        });
        setToken(newToken);
    }, []);
    const isComplete = token.every(digit => digit !== '') && token.join('').length === TOKEN_LENGTH;
    const handleContinue = useCallback(async () => {
        if (!isComplete || !canSendToken) {
            return;
        }
        setTokenError(undefined);
        try {
            await hasValidToken({
                token: token.join(''),
                deviceId: deviceId,
                templateId: TemplateEnum.RESET_PASSWORD,
                documentNumber: cleanDocumentNumber,
            }).unwrap();
            onContinue();
        }
        catch {
            setTokenError(t('reset-password.token.invalid'));
        }
    }, [isComplete, canSendToken, deviceId, cleanDocumentNumber, token, hasValidToken, onContinue, t]);
    const sendTokenByChannel = useCallback(async (channel) => {
        if (!canSendToken)
            return;
        const tokenPayload = {
            deviceId: deviceId,
            templateId: TemplateEnum.RESET_PASSWORD,
            documentNumber: cleanDocumentNumber,
            channel,
        };
        try {
            if (channel === ChannelEnum.email) {
                await sendEmailToken(tokenPayload).unwrap();
            }
            else if (channel === ChannelEnum.phone) {
                await sendPhoneToken(tokenPayload).unwrap();
            }
            else {
                await sendWhatsappToken(tokenPayload).unwrap();
            }
            resetCountdown();
        }
        catch {
        }
    }, [canSendToken, deviceId, cleanDocumentNumber, sendEmailToken, sendPhoneToken, sendWhatsappToken, resetCountdown]);
    const handleResendCode = useCallback(async () => {
        if (isSmsSource) {
            const channel = isWhatsappMode ? ChannelEnum.whatsapp : ChannelEnum.phone;
            await sendTokenByChannel(channel);
        }
        else {
            await sendTokenByChannel(ChannelEnum.email);
        }
    }, [isSmsSource, isWhatsappMode, sendTokenByChannel]);
    const handleToggleChannel = useCallback(async () => {
        const nextChannel = isWhatsappMode ? ChannelEnum.phone : ChannelEnum.whatsapp;
        await sendTokenByChannel(nextChannel);
        setIsWhatsappMode(!isWhatsappMode);
    }, [isWhatsappMode, sendTokenByChannel]);
    const isResending = isResendingEmail || isResendingPhone;
    const isLoading = isValidating || isResending || isResendingWhatsapp;
    return {
        token,
        countdown,
        isCounting,
        tokenError,
        isComplete,
        maskedEmail,
        maskedPhone,
        isSmsSource,
        isWhatsappMode,
        isResendingEmail,
        isResendingPhone,
        isResendingWhatsapp,
        isResending,
        isValidating,
        isLoading,
        tokenLength: TOKEN_LENGTH,
        getInputId,
        handleTokenChange,
        handleKeyDown,
        handlePaste,
        handleContinue,
        handleResendCode,
        handleToggleChannel,
    };
}
